import React, { useState, useEffect } from 'react';
import swal from 'sweetalert';
import {
    getReviews,
    fetchReviewsAsync,
    addReviewAsync,
    getMenuItems,
    fetchMenuItemsAsync
} from '../../services/storage';

const STAR_LABELS = {
    1: 'Tạm được (1/5)',
    2: 'Bình thường (2/5)',
    3: 'Hài lòng (3/5)',
    4: 'Rất tốt (4/5)',
    5: 'Tuyệt vời & Đậm vị (5/5)'
};

const Reviews = () => {
    const [reviews, setReviews] = useState(getReviews());
    const [menuItems, setMenuItems] = useState(getMenuItems());
    const [selectedStarFilter, setSelectedStarFilter] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Review form state
    const [hoverStar, setHoverStar] = useState(5);
    const [formData, setFormData] = useState({
        name: '',
        role: 'Du khách',
        stars: 5,
        favoriteDrink: '',
        comment: ''
    });

    const loadData = async () => {
        const [revsData, menuData] = await Promise.all([
            fetchReviewsAsync(),
            fetchMenuItemsAsync()
        ]);
        setReviews(revsData);
        if (menuData && menuData.length > 0) {
            setMenuItems(menuData);
            setFormData(prev => ({
                ...prev,
                favoriteDrink: prev.favoriteDrink || menuData[0].name
            }));
        }
    };

    useEffect(() => {
        loadData();

        const handleReviewsUpdate = () => {
            setReviews(getReviews());
        };
        const handleMenuUpdate = () => {
            const currentMenu = getMenuItems();
            setMenuItems(currentMenu);
            if (currentMenu && currentMenu.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    favoriteDrink: prev.favoriteDrink || currentMenu[0].name
                }));
            }
        };

        window.addEventListener('cafe9_reviews_updated', handleReviewsUpdate);
        window.addEventListener('cafe9_menu_updated', handleMenuUpdate);
        return () => {
            window.removeEventListener('cafe9_reviews_updated', handleReviewsUpdate);
            window.removeEventListener('cafe9_menu_updated', handleMenuUpdate);
        };
    }, []);

    // Metrics calculations
    const totalReviews = reviews.length;
    const avgScore = totalReviews > 0
        ? (reviews.reduce((sum, item) => sum + item.stars, 0) / totalReviews).toFixed(1)
        : '5.0';

    const starCounts = {
        5: reviews.filter(r => r.stars === 5).length,
        4: reviews.filter(r => r.stars === 4).length,
        3: reviews.filter(r => r.stars === 3).length,
        2: reviews.filter(r => r.stars === 2).length,
        1: reviews.filter(r => r.stars === 1).length,
    };

    const filteredReviews = selectedStarFilter === 'all'
        ? reviews
        : reviews.filter(r => r.stars === Number(selectedStarFilter));

    const checkReviewLimit = () => {
        const lastTime = localStorage.getItem('cafe9_last_review_time');
        if (lastTime) {
            const timePassed = Date.now() - Number(lastTime);
            const cooldown = 24 * 60 * 60 * 1000; // 24 hours
            if (timePassed < cooldown) {
                const hoursRemaining = Math.ceil((cooldown - timePassed) / (60 * 60 * 1000));
                return { isLimited: true, hoursRemaining };
            }
        }
        return { isLimited: false, hoursRemaining: 0 };
    };

    const handleOpenReviewModal = () => {
        const limit = checkReviewLimit();
        if (limit.isLimited) {
            swal({
                title: "Cảm Ơn Bạn!",
                text: `Bạn đã gửi đánh giá cho Café 9 gần đây rồi. \n Để đảm bảo tính công tâm, mỗi khách hàng chỉ gửi 1 đánh giá trong 24 giờ. \n Bạn có thể gửi đánh giá mới sau khoảng ${limit.hoursRemaining} giờ nữa nhé!`,
                icon: "info",
                button: "Đã Hiểu"
            });
            return;
        }
        setIsModalOpen(true);
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleStarClick = (starValue) => {
        setFormData({ ...formData, stars: starValue });
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        const limit = checkReviewLimit();
        if (limit.isLimited) {
            swal("Thông Báo", `Bạn đã gửi đánh giá trong ngày rồi. Vui lòng quay lại sau ${limit.hoursRemaining} giờ nhé!`, "info");
            setIsModalOpen(false);
            return;
        }

        if (!formData.name.trim() || !formData.comment.trim()) {
            swal("Lưu ý", "Vui lòng nhập họ tên và nội dung cảm nhận của bạn.", "warning");
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await addReviewAsync(formData);
            if (result && result.status === 'error') {
                swal("Thông Báo", result.message || "Không thể gửi đánh giá lúc này.", "warning");
                return;
            }

            // Save review timestamp to LocalStorage (Layer 1)
            localStorage.setItem('cafe9_last_review_time', Date.now().toString());

            swal({
                title: "Cảm Ơn Bạn!",
                text: "Đánh giá của bạn đã được ghi nhận thành công vào hệ thống. Café 9 rất trân trọng tình cảm của bạn!",
                icon: "success",
                button: "Tuyệt Vời"
            });

            setIsModalOpen(false);
            setFormData({
                name: '',
                role: 'Du khách',
                stars: 5,
                favoriteDrink: menuItems.length > 0 ? menuItems[0].name : 'Cà Phê Sữa Đá',
                comment: '',
                bot_honey: ''
            });
        } catch (err) {
            swal("Thông Báo", "Đã tiếp nhận đánh giá của bạn!", "success");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="reviews-section" id="reviews">
            <div className="section-header">
                <span className="section-tag">Trải Nghiệm Khách Hàng</span>
                <h2>Đánh Giá & Cảm Nhận Về <span>Café 9</span></h2>
                <p>Những chia sẻ chân thật từ những người yêu thích hương vị cà phê phin nguyên bản tại Phước Hải.</p>
                <div className="header-divider">
                    <i className="fas fa-comments"></i>
                </div>
            </div>

            {/* Metrics Overview Box */}
            <div className="review-stats-banner">
                <div className="stats-score-box">
                    <div className="score-number">{avgScore}</div>
                    <div className="score-stars">
                        {[...Array(5)].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                        ))}
                    </div>
                    <div className="score-count">Dựa trên {totalReviews} lượt đánh giá thực tế</div>
                </div>

                <div className="stats-breakdown">
                    {[5, 4, 3, 2, 1].map(stars => {
                        const count = starCounts[stars] || 0;
                        const pct = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
                        return (
                            <div key={stars} className="breakdown-row">
                                <span className="star-label">{stars} <i className="fas fa-star"></i></span>
                                <div className="bar-bg">
                                    <div className="bar-fill" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="bar-pct">{count} ({pct}%)</span>
                            </div>
                        );
                    })}
                </div>

                <div className="stats-cta-box">
                    <div className="cta-highlight">
                        <i className="fas fa-shield-heart"></i>
                        <span>100% Cà phê nguyên chất</span>
                    </div>
                    <p>Bạn đã từng ghé thăm và thưởng thức cà phê tại Café 9?</p>
                    <button className="btn btn-primary" onClick={handleOpenReviewModal}>
                        <i className="fas fa-pen-to-square"></i>
                        <span>Viết Đánh Giá Ngay</span>
                    </button>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="review-filter-tabs">
                <button
                    className={`rev-filter-btn ${selectedStarFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedStarFilter('all')}
                >
                    Tất Cả ({reviews.length})
                </button>
                <button
                    className={`rev-filter-btn ${selectedStarFilter === '5' ? 'active' : ''}`}
                    onClick={() => setSelectedStarFilter('5')}
                >
                    5 Sao ⭐ ({starCounts[5]})
                </button>
                <button
                    className={`rev-filter-btn ${selectedStarFilter === '4' ? 'active' : ''}`}
                    onClick={() => setSelectedStarFilter('4')}
                >
                    4 Sao ⭐ ({starCounts[4]})
                </button>
            </div>

            {/* Reviews Grid List */}
            <div className="reviews-grid">
                {filteredReviews.map(review => (
                    <div key={review.id} className="review-card">
                        <div className="review-card-top">
                            <div className="review-stars">
                                {[...Array(review.stars)].map((_, i) => (
                                    <i key={i} className="fas fa-star"></i>
                                ))}
                            </div>
                            <span className="review-date">{review.date}</span>
                        </div>

                        {review.favoriteDrink && (
                            <div className="review-drink-chip">
                                <i className="fas fa-mug-saucer"></i> Món uống: <strong>{review.favoriteDrink}</strong>
                            </div>
                        )}

                        <p className="review-comment">"{review.comment}"</p>

                        <div className="review-author-row">
                            <div className="review-avatar">
                                {review.avatar || review.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h4 className="author-name">
                                    {review.name}
                                    {review.isVerified && (
                                        <i className="fas fa-circle-check verified-badge" title="Đã ghé quán"></i>
                                    )}
                                </h4>
                                <span className="author-role">{review.role}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal Form: Write Review */}
            {isModalOpen && (
                <div className="review-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="review-modal-card" onClick={e => e.stopPropagation()}>
                        <button className="review-modal-close" onClick={() => setIsModalOpen(false)} aria-label="Đóng">
                            &times;
                        </button>

                        <div className="modal-header-box">
                            <div className="modal-badge">
                                <i className="fas fa-star"></i> Chia Sẻ Trải Nghiệm
                            </div>
                            <h3>Đánh Giá Café 9 Phước Hải</h3>
                            <p>Cảm nhận của bạn là nguồn động lực quý báu để quán ngày càng phục vụ chu đáo hơn.</p>
                        </div>

                        <form onSubmit={handleSubmitReview}>
                            {/* Honeypot Anti-Bot Field (Hidden) */}
                            <input
                                type="text"
                                name="bot_honey"
                                value={formData.bot_honey || ''}
                                onChange={handleFormChange}
                                style={{ display: 'none' }}
                                tabIndex="-1"
                                autoComplete="off"
                            />

                            {/* Interactive Star Rating */}
                            <div className="form-group star-picker-group">
                                <label className="form-label">Chọn mức độ hài lòng của bạn *</label>
                                <div className="interactive-stars">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <i
                                            key={star}
                                            className={`fas fa-star star-item ${star <= (hoverStar || formData.stars) ? 'active' : ''}`}
                                            onMouseEnter={() => setHoverStar(star)}
                                            onMouseLeave={() => setHoverStar(formData.stars)}
                                            onClick={() => handleStarClick(star)}
                                        ></i>
                                    ))}
                                </div>
                                <span className="star-status-text">{STAR_LABELS[hoverStar || formData.stars]}</span>
                            </div>

                            <div className="form-row-2">
                                <div className="form-group">
                                    <label className="form-label" htmlFor="revName">Họ và tên của bạn *</label>
                                    <input
                                        type="text"
                                        id="revName"
                                        name="name"
                                        required
                                        className="form-control"
                                        placeholder="Ví dụ: Hoàng Minh"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" htmlFor="revRole">Bạn là?</label>
                                    <select
                                        id="revRole"
                                        name="role"
                                        className="form-control"
                                        value={formData.role}
                                        onChange={handleFormChange}
                                    >
                                        <option value="Du khách">Du khách ghé thăm</option>
                                        <option value="Khách quen địa phương">Khách quen Phước Hải</option>
                                        <option value="Bạn trẻ du lịch">Bạn trẻ du lịch</option>
                                        <option value="Gia đình">Đi cùng gia đình</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="revDrink">Món thức uống bạn đã thưởng thức:</label>
                                <select
                                    id="revDrink"
                                    name="favoriteDrink"
                                    className="form-control"
                                    value={formData.favoriteDrink}
                                    onChange={handleFormChange}
                                >
                                    {menuItems && menuItems.length > 0 ? (
                                        menuItems
                                            .filter(item => item.isAvailable !== false)
                                            .map(item => (
                                                <option key={item.id} value={item.name}>
                                                    {item.name} {item.price ? `(${item.price})` : ''}
                                                </option>
                                            ))
                                    ) : (
                                        <>
                                            <option value="Cà Phê Đen Phin">Cà Phê Đen Phin</option>
                                            <option value="Cà Phê Sữa Đá">Cà Phê Sữa Đá</option>
                                            <option value="Bạc Xỉu Đá 3 Tầng">Bạc Xỉu Đá 3 Tầng</option>
                                        </>
                                    )}
                                    <option value="Món Khác">Món khác tại quán</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="revComment">Cảm nhận chi tiết của bạn về hương vị & không gian quán *</label>
                                <textarea
                                    id="revComment"
                                    name="comment"
                                    required
                                    className="form-control"
                                    placeholder="Chia sẻ về hương vị cà phê phin, không gian quán, thái độ phục vụ..."
                                    value={formData.comment}
                                    onChange={handleFormChange}
                                ></textarea>
                            </div>

                            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }} disabled={isSubmitting}>
                                <i className="fas fa-paper-plane"></i>
                                <span>{isSubmitting ? "Đang gửi..." : "Gửi Đánh Giá Của Bạn"}</span>
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Reviews;
